import type {
	BuildTimelineResult,
	CalculationResult,
	MaterialPhase,
	ThermalInputs,
} from "../../engines/types";
import { getEngine } from "../../engines/engineRegistry";
import { getRuleValue } from "../../data/ruleRegistry";
import { EXTERIOR_TREATMENT_MATERIAL_KEY } from "../finishes/calculateExteriorTreatment";
import { getT, type Dictionary } from "../../i18n";
import type { WallOpening } from "../framing/generateFramedWall";
import type { Market } from "../location/types";
import { createBuildGuideSubstepId } from "./buildProgress";
import type {
	BuildGuideDictionaryKey,
	BuildGuideCheckpointDraft,
	BuildGuideSubstepDraft,
	BuildGuideText,
	BuildGuideTextArgs,
	ProjectBuildGuideStepDraft,
} from "./buildGuideDraft";
import {
	buildGuideText,
	formatGuideRow,
	insertSubstepsAfterId,
	isGuideRow,
} from "./buildGuideDraft";
import type { ToolGroup } from "./toolLibrary";
import { getBadge, getTools } from "./toolLibrary";

export { insertSubstepsAfterId };
export type { ToolGroup };
export type {
	BuildGuideCheckpointDraft,
	BuildGuideSubstepDraft,
	BuildGuideText,
	BuildGuideTextToken,
	ProjectBuildGuideStepDraft,
} from "./buildGuideDraft";

export interface BuildGuideCrewRequirement {
	size: "solo" | "duo";
	reasonKey: BuildGuideDictionaryKey;
	reason: string;
	reasonText?: BuildGuideText;
}

export interface BuildGuideMistakeCallout {
	severity: "safety" | "quality";
	wrong: string;
	wrongText?: BuildGuideText;
	right: string;
	rightText?: BuildGuideText;
}

export interface BuildGuideCheckpoint {
	id: string;
	label: string;
	labelText?: BuildGuideText;
	detail?: string;
	detailText?: BuildGuideText;
}

export interface BuildGuideSubstep {
	id: string;
	title: string;
	titleText?: BuildGuideText;
	purpose?: string;
	purposeText?: BuildGuideText;
	checkpoints: BuildGuideCheckpoint[];
	crew?: BuildGuideCrewRequirement;
	mistakeCallout?: BuildGuideMistakeCallout;
	tools?: ToolGroup[];
	materialsSummary?: string[];
	materialSummaryTexts?: BuildGuideText[];
	measurements?: string[];
	measurementTexts?: BuildGuideText[];
}

export interface BuildGuideStepProgress {
	completed: boolean;
	completedCheckpointIds: string[];
	completedSubstepIds?: string[];
}

export type BuildProgress = Record<string, BuildGuideStepProgress>;

export interface BuildGuideLeadTimeRange {
	min: number;
	max: number;
}

export interface ProjectBuildGuideStep {
	nodeType: "step" | "gate";
	id: string;
	title: string;
	titleText?: BuildGuideText;
	purpose: string;
	purposeText?: BuildGuideText;
	prerequisites: string[];
	prerequisiteTexts?: BuildGuideText[];
	substeps?: BuildGuideSubstep[];
	checkpoints: BuildGuideCheckpoint[];
	materialsSummary: string[];
	materialSummaryTexts?: BuildGuideText[];
	cutSummary: string[];
	cutSummaryTexts?: BuildGuideText[];
	risks: string[];
	riskTexts?: BuildGuideText[];
	controls: string[];
	controlTexts?: BuildGuideText[];
	tools: ToolGroup[];
	dependencyIds: string[];
	timeline?: BuildTimelineResult["steps"][number];
	professionalBadge: string;
	professionalReason: string;
	kind?: "booking" | "inspection" | "permit";
	blocksStepId?: string;
	checklist?: BuildGuideCheckpoint[];
	leadTimeLabel?: string;
	leadTimeLabelText?: BuildGuideText;
	leadTimeWeeks?: BuildGuideLeadTimeRange;
}

export interface BuildGuideGate extends ProjectBuildGuideStep {
	nodeType: "gate";
	kind: "booking" | "inspection" | "permit";
	blocksStepId: string;
	checklist: BuildGuideCheckpoint[];
	leadTimeLabel: string;
	leadTimeLabelText?: BuildGuideText;
	leadTimeWeeks?: BuildGuideLeadTimeRange;
}

export type BuildGuideNode = ProjectBuildGuideStep | BuildGuideGate;

export interface ProjectBuildGuide {
	kickoff: {
		title: string;
		titleText?: BuildGuideText;
		summary: string;
		summaryText?: BuildGuideText;
		blockers: string[];
		blockerTexts?: BuildGuideText[];
		highlights: Array<{
			label: string;
			labelText?: BuildGuideText;
			value: string;
			valueText?: BuildGuideText;
		}>;
	};
	totals: {
		areaSqm: number;
		totalHours: number | null;
		weekendEstimate: number | null;
		calendarEstimateLabel?: string;
		leadTimeWeeks?: BuildGuideLeadTimeRange | null;
		professionalStepCount: number;
		estimatedProfessionalCostSek: number | null;
		openingCount: number;
	};
	steps: ProjectBuildGuideStep[];
}

type BuildGuideSubstepInput = Omit<BuildGuideSubstepDraft, "id">;

interface BuildGuideGateDraft
	extends Omit<
		BuildGuideGate,
		| "nodeType"
		| "title"
		| "titleText"
		| "purpose"
		| "purposeText"
		| "prerequisites"
		| "prerequisiteTexts"
		| "checklist"
		| "checkpoints"
		| "leadTimeLabel"
		| "leadTimeLabelText"
		| "materialsSummary"
		| "materialSummaryTexts"
		| "cutSummary"
		| "cutSummaryTexts"
		| "risks"
		| "riskTexts"
		| "controls"
		| "controlTexts"
	> {
	title: BuildGuideText;
	purpose: BuildGuideText;
	prerequisites?: BuildGuideText[];
	checklist: BuildGuideCheckpointDraft[];
	leadTimeLabel: BuildGuideText;
	materialsSummary?: BuildGuideText[];
	cutSummary?: BuildGuideText[];
	risks?: BuildGuideText[];
	controls?: BuildGuideText[];
}

function applyTextArgs(template: string, args?: BuildGuideTextArgs): string {
	if (!args) return template;
	return Object.entries(args).reduce(
		(result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
		template,
	);
}

function toLegacyGuideText(value: BuildGuideText, t: Dictionary): string {
	if (typeof value === "string") return value;
	if (isGuideRow(value)) return formatGuideRow(value);
	return applyTextArgs(t[value.key], value.args);
}

function maybeGuideText(value: BuildGuideText | undefined): BuildGuideText | undefined {
	return typeof value === "string" ? undefined : value;
}

function serializeBuildGuideText(value: BuildGuideText): string {
	if (typeof value === "string") return value;
	if (isGuideRow(value)) return formatGuideRow(value);
	return JSON.stringify({ key: value.key, args: value.args ?? {} });
}

function uniqueGuideText(items: BuildGuideText[]): BuildGuideText[] {
	const seen = new Set<string>();
	const next: BuildGuideText[] = [];

	for (const item of items) {
		if (typeof item === "string" && item.length === 0) continue;
		const serialized = serializeBuildGuideText(item);
		if (seen.has(serialized)) continue;
		seen.add(serialized);
		next.push(item);
	}

	return next;
}

function createChecklistDrafts(
	idPrefix: string,
	items: BuildGuideText[],
): BuildGuideCheckpointDraft[] {
	return uniqueGuideText(items).map((label, index) => ({
		id: `${idPrefix}-${String(index + 1).padStart(2, "0")}`,
		label,
	}));
}

type MaterialSummaryRow = Pick<
	CalculationResult["materials"][number],
	"material" | "dimension" | "quantity"
>;

function formatMaterialSummaryRow(row: MaterialSummaryRow): string {
	return `${row.material} ${row.dimension} • ${row.quantity}`;
}

function uniqueMaterialSummaryRows(
	rows: MaterialSummaryRow[],
): MaterialSummaryRow[] {
	const seen = new Set<string>();
	return rows.filter((row) => {
		const serialized = formatMaterialSummaryRow(row);
		if (seen.has(serialized)) return false;
		seen.add(serialized);
		return true;
	});
}

function getPhaseRows(
	data: CalculationResult,
	phase: MaterialPhase,
): MaterialSummaryRow[] {
	const phaseGroups = data.materialsByPhase?.filter(
		(candidate) => candidate.phase === phase,
	);
	if (phaseGroups && phaseGroups.length > 0) {
		return uniqueMaterialSummaryRows(
			phaseGroups.flatMap((group) =>
				group.rows.map((row) => ({
					material: row.material,
					dimension: row.dimension,
					quantity: row.quantity,
				})),
			),
		);
	}
	return uniqueMaterialSummaryRows(
		data.materials
			.filter((row) => row.phase === phase)
			.map((row) => ({
				material: row.material,
				dimension: row.dimension,
				quantity: row.quantity,
			})),
	);
}

const SUMMARY_ROW_CAP = 3;

/**
 * Cap a serialized summary list at {@link SUMMARY_ROW_CAP} rows, but when rows
 * are dropped append a visible "+ N more" token instead of silently truncating
 * (Issue 106). The overflow token carries the remaining count as an arg so the
 * UI/PDF render it localized.
 */
function withOverflowNotice(
	rendered: BuildGuideText[],
	totalCount: number,
): BuildGuideText[] {
	const hidden = totalCount - rendered.length;
	if (hidden <= 0) return rendered;
	return [...rendered, buildGuideText("domain_build_guide_moreRows", { count: hidden })];
}

function summarizeMaterialRows(rows: MaterialSummaryRow[]): BuildGuideText[] {
	return withOverflowNotice(
		rows.slice(0, SUMMARY_ROW_CAP).map((row) => ({
			materialRow: {
				material: row.material,
				dimension: row.dimension,
				quantity: row.quantity,
			},
		})),
		rows.length,
	);
}

function summarizePhaseMatches(
	data: CalculationResult,
	phase: MaterialPhase,
	patterns: RegExp[],
	fallback?: BuildGuideText,
): BuildGuideText[] {
	const matches = getPhaseRows(data, phase).filter((row) =>
		patterns.some((pattern) =>
			pattern.test(`${row.material} ${row.dimension}`.toLowerCase()),
		),
	);
	if (matches.length > 0) return summarizeMaterialRows(matches);
	return fallback ? [fallback] : [];
}

/**
 * The facade-treatment rows, found by `materialKey`.
 *
 * Reads `data.materials` rather than `getPhaseRows`: that helper projects rows
 * down to material/dimension/quantity and drops `materialKey` on the way — the
 * projection trap the domain-key-fields note is about.
 */
/**
 * Fold an optional extra timeline step into a base one: hours add, and the
 * extra's weather-critical flag and drying wait carry over. Returns the base
 * untouched when there is no extra.
 */
function mergeTimelineSteps(
	base: BuildTimelineResult["steps"][number] | undefined,
	extra: BuildTimelineResult["steps"][number] | undefined,
): BuildTimelineResult["steps"][number] | undefined {
	if (!base || !extra) return base;
	return {
		...base,
		hours: base.hours + extra.hours,
		weatherCritical: base.weatherCritical || extra.weatherCritical,
		weatherCriticalReason:
			base.weatherCriticalReason ?? extra.weatherCriticalReason,
		dryingHours: extra.dryingHours,
	};
}

function summarizeTreatmentMaterials(
	data: CalculationResult,
): BuildGuideText[] {
	return summarizeMaterialRows(
		data.materials
			.filter((row) => row.materialKey === EXTERIOR_TREATMENT_MATERIAL_KEY)
			.map((row) => ({
				material: row.material,
				dimension: row.dimension,
				quantity: row.quantity,
			})),
	);
}

function summarizeRowsWithFallback(
	rows: MaterialSummaryRow[],
	fallback?: BuildGuideText,
): BuildGuideText[] {
	if (rows.length > 0) return summarizeMaterialRows(rows);
	return fallback ? [fallback] : [];
}

function summarizePhase(
	data: CalculationResult,
	phase: MaterialPhase,
	fallback: BuildGuideText,
): BuildGuideText[] {
	const group = data.materialsByPhase?.find(
		(candidate) => candidate.phase === phase,
	);
	if (group && group.rows.length > 0) {
		return withOverflowNotice(
			group.rows
				.slice(0, SUMMARY_ROW_CAP)
				.map((row) => ({
					materialRow: {
						material: row.material,
						dimension: row.dimension,
						quantity: row.quantity,
					},
				})),
			group.rows.length,
		);
	}

	const rows = data.materials.filter((row) => row.phase === phase);
	if (rows.length > 0) {
		return withOverflowNotice(
			rows
				.slice(0, SUMMARY_ROW_CAP)
				.map((row) => ({
					materialRow: {
						material: row.material,
						dimension: row.dimension,
						quantity: row.quantity,
					},
				})),
			rows.length,
		);
	}

	return [fallback];
}

function summarizeCuts(
	data: CalculationResult,
	patterns: string[],
	fallback: BuildGuideText,
): BuildGuideText[] {
	const matches = data.cuts.filter((row) =>
		patterns.some((pattern) => row.part.toLowerCase().includes(pattern)),
	);
	if (matches.length > 0) {
		return withOverflowNotice(
			matches
				.slice(0, SUMMARY_ROW_CAP)
				.map((row) => ({
					cutRow: {
						part: row.part,
						count: row.count,
						cutLength: row.cutLength,
					},
				})),
			matches.length,
		);
	}
	return [fallback];
}

function summarizeFacadeMaterials(data: CalculationResult): BuildGuideText[] {
	const rows = summarizePhase(
		data,
		"roofExterior",
		buildGuideText("domain_build_guide_facadeFallbackMaterials"),
	);
	const openingTrimRow = data.materials.find(
		(row) =>
			row.phase === "roofExterior" &&
			row.material.toLowerCase().includes("foder"),
	);

	if (!openingTrimRow) {
		return rows;
	}

	const trim: BuildGuideText = {
		materialRow: {
			material: openingTrimRow.material,
			dimension: openingTrimRow.dimension,
			quantity: openingTrimRow.quantity,
		},
	};
	const serialized = serializeBuildGuideText(trim);
	return rows.some((row) => serializeBuildGuideText(row) === serialized)
		? rows
		: [...rows, trim];
}

/**
 * Surface `data.foundation.plinths[].status === "warning"` as a build-guide
 * measurement (Issue 106). The rich guide previously ignored these — only the
 * retired legacy generator read them. We aggregate by warning reason so a slope
 * that flags many piers renders one "N piers: <reason>" line, not N lines.
 * Returns [] when the foundation slice is absent or all piers are OK.
 */
function summarizePlinthWarnings(data: CalculationResult): BuildGuideText[] {
	const plinths = data.foundation?.plinths ?? [];
	const warningPlinths = plinths.filter((plinth) => plinth.status === "warning");
	if (warningPlinths.length === 0) return [];

	const messageKeyByReason: Record<
		"domain_found_warningGroundHigh" | "domain_found_warningPlinthShort",
		BuildGuideDictionaryKey
	> = {
		domain_found_warningGroundHigh:
			"domain_build_guide_foundationPlinthWarningGroundHigh",
		domain_found_warningPlinthShort:
			"domain_build_guide_foundationPlinthWarningPlinthShort",
	};

	const byReasonKey = new Map<BuildGuideDictionaryKey, number>();
	for (const plinth of warningPlinths) {
		if (!plinth.warningReasonKey) continue;
		const messageKey = messageKeyByReason[plinth.warningReasonKey];
		byReasonKey.set(messageKey, (byReasonKey.get(messageKey) ?? 0) + 1);
	}

	return [...byReasonKey.entries()].map(([messageKey, count]) =>
		buildGuideText(messageKey, { count }),
	);
}

function createSubsteps(
	stepId: string,
	substeps: BuildGuideSubstepInput[],
): BuildGuideSubstepDraft[] {
	return substeps.map((substep, index) => ({
		...substep,
		id: createBuildGuideSubstepId(stepId, index),
	}));
}

function serializeCheckpoint(
	checkpoint: BuildGuideCheckpointDraft,
	t: Dictionary,
): BuildGuideCheckpoint {
	return {
		id: checkpoint.id,
		label: toLegacyGuideText(checkpoint.label, t),
		labelText: maybeGuideText(checkpoint.label),
		detail: checkpoint.detail
			? toLegacyGuideText(checkpoint.detail, t)
			: undefined,
		detailText: maybeGuideText(checkpoint.detail),
	};
}

function serializeSubstep(
	substep: BuildGuideSubstepDraft,
	t: Dictionary,
): BuildGuideSubstep {
	return {
		id: substep.id,
		title: toLegacyGuideText(substep.title, t),
		titleText: maybeGuideText(substep.title),
		purpose: substep.purpose
			? toLegacyGuideText(substep.purpose, t)
			: undefined,
		purposeText: maybeGuideText(substep.purpose),
		checkpoints: substep.checkpoints.map((checkpoint) =>
			serializeCheckpoint(checkpoint, t),
		),
		crew: substep.crew
			? {
					size: substep.crew.size,
					reasonKey: substep.crew.reason.key,
					reason: toLegacyGuideText(substep.crew.reason, t),
					reasonText: substep.crew.reason,
				}
			: undefined,
		mistakeCallout: substep.mistakeCallout
			? {
					severity: substep.mistakeCallout.severity,
					wrong: toLegacyGuideText(substep.mistakeCallout.wrong, t),
					wrongText: maybeGuideText(substep.mistakeCallout.wrong),
					right: toLegacyGuideText(substep.mistakeCallout.right, t),
					rightText: maybeGuideText(substep.mistakeCallout.right),
				}
			: undefined,
		tools: substep.tools,
		materialsSummary: substep.materialsSummary?.map((value) =>
			toLegacyGuideText(value, t),
		),
		materialSummaryTexts: substep.materialsSummary,
		measurements: substep.measurements?.map((value) =>
			toLegacyGuideText(value, t),
		),
		measurementTexts: substep.measurements,
	};
}

function createGuideStep(
	step: Omit<ProjectBuildGuideStepDraft, "checkpoints" | "substeps"> & {
		substeps: BuildGuideSubstepInput[];
	},
): ProjectBuildGuideStepDraft {
	const substeps = createSubsteps(step.id, step.substeps);

	return {
		...step,
		substeps,
	};
}

function serializeGuideStep(
	step: ProjectBuildGuideStepDraft,
	t: Dictionary,
): ProjectBuildGuideStep {
	const serializedSubsteps = step.substeps.map((substep) => serializeSubstep(substep, t));

	return {
		...step,
		nodeType: "step",
		title: toLegacyGuideText(step.title, t),
		titleText: maybeGuideText(step.title),
		purpose: toLegacyGuideText(step.purpose, t),
		purposeText: maybeGuideText(step.purpose),
		prerequisites: step.prerequisites.map((value) => toLegacyGuideText(value, t)),
		prerequisiteTexts: step.prerequisites,
		substeps: serializedSubsteps,
		checkpoints: serializedSubsteps.flatMap((substep) => substep.checkpoints),
		materialsSummary: step.materialsSummary.map((value) =>
			toLegacyGuideText(value, t),
		),
		materialSummaryTexts: step.materialsSummary,
		cutSummary: step.cutSummary.map((value) => toLegacyGuideText(value, t)),
		cutSummaryTexts: step.cutSummary,
		risks: step.risks.map((value) => toLegacyGuideText(value, t)),
		riskTexts: step.risks,
		controls: step.controls.map((value) => toLegacyGuideText(value, t)),
		controlTexts: step.controls,
	};
}

function createGuideGate(
	gate: BuildGuideGateDraft,
	t: Dictionary,
): BuildGuideGate {
	const checklist = gate.checklist.map((checkpoint) =>
		serializeCheckpoint(checkpoint, t),
	);

	return {
		...gate,
		nodeType: "gate",
		title: toLegacyGuideText(gate.title, t),
		titleText: maybeGuideText(gate.title),
		purpose: toLegacyGuideText(gate.purpose, t),
		purposeText: maybeGuideText(gate.purpose),
		prerequisites: (gate.prerequisites ?? []).map((value) =>
			toLegacyGuideText(value, t),
		),
		prerequisiteTexts: gate.prerequisites ?? [],
		checklist,
		checkpoints: checklist,
		leadTimeLabel: toLegacyGuideText(gate.leadTimeLabel, t),
		leadTimeLabelText: maybeGuideText(gate.leadTimeLabel),
		materialsSummary: (gate.materialsSummary ?? []).map((value) =>
			toLegacyGuideText(value, t),
		),
		materialSummaryTexts: gate.materialsSummary ?? [],
		cutSummary: (gate.cutSummary ?? []).map((value) =>
			toLegacyGuideText(value, t),
		),
		cutSummaryTexts: gate.cutSummary ?? [],
		risks: (gate.risks ?? []).map((value) => toLegacyGuideText(value, t)),
		riskTexts: gate.risks ?? [],
		controls: (gate.controls ?? []).map((value) =>
			toLegacyGuideText(value, t),
		),
		controlTexts: gate.controls ?? [],
	};
}

function insertGatesBeforeSteps(
	steps: ProjectBuildGuideStep[],
	gates: BuildGuideGate[],
): ProjectBuildGuideStep[] {
	if (gates.length === 0) return steps;

	const gatesByBlockedStep = new Map<string, BuildGuideGate[]>();
	for (const gate of gates) {
		const existing = gatesByBlockedStep.get(gate.blocksStepId) ?? [];
		existing.push(gate);
		gatesByBlockedStep.set(gate.blocksStepId, existing);
	}

	const nodes: ProjectBuildGuideStep[] = [];
	for (const step of steps) {
		nodes.push(...(gatesByBlockedStep.get(step.id) ?? []));
		nodes.push(step);
	}

	// A gate whose step an engine removed (the indoor sauna has no
	// foundation step, issue 156) still blocks the build: it goes first
	// rather than silently disappearing with its anchor.
	const stepIds = new Set(steps.map((step) => step.id));
	const orphaned = gates.filter((gate) => !stepIds.has(gate.blocksStepId));
	return [...orphaned, ...nodes];
}

export function getProjectBuildGuide(input: {
	data: CalculationResult;
	width: number;
	length: number;
	openings: WallOpening[];
	thermalInputs: ThermalInputs;
	market?: Market;
	engineType?: string;
}): ProjectBuildGuide {
	const {
		data,
		width,
		length,
		openings,
		thermalInputs,
		market = "se",
		engineType,
	} = input;
	const t = getT(market);
	// Building-permit requirements are jurisdiction-specific Swedish law
	// (PBL/Boverket, eldstadsanmälan). The intl market must not surface m²
	// permit thresholds, boundary rules, or fireplace-notification gates as
	// if they were universal — suppress every permit-derived surface there.
	const permitData = market === "intl" ? undefined : data.permit;
	const timelineById = new Map(
		(data.timeline?.steps ?? []).map((step) => [step.id, step]),
	);
	const areaSqm = Number((width * length).toFixed(1));
	const openingCount = openings.length;
	const permitMessages = permitData?.messages ?? [];
	const permitChecklist = permitData?.checklist ?? [];
	const placementChecklist = data.placement?.checklist ?? [];
	const professionalWarnings = (data.professionalGuidance?.steps ?? []).flatMap(
		(step) =>
			step.requiresProfessional ? [`${step.label}: ${step.reason}`] : [],
	);
	const hasPartitionBlocking =
		(data.partitionWallFraming?.requiredBlockingCount ?? 0) > 0;
	const roofKind = data.roofAssembly?.kind;
	const hasDoorOpening = openings.some((opening) => opening.type === "door");
	// Issue 134: a facade colour was chosen iff the BOM carries its litres.
	const hasExteriorTreatment = (data.materials ?? []).some(
		(row) => row.materialKey === EXTERIOR_TREATMENT_MATERIAL_KEY,
	);
	// Coating the facade is a SUBSTEP of the facade step, so its hours and its
	// drying wait belong to that node's badge. `estimateBuildTime` emits it as
	// its own timeline step, and nothing was attaching that step to a guide
	// node — the work and the 6–24 h cure were both invisible in the guide.
	const facadeTimeline = mergeTimelineSteps(
		timelineById.get("facade"),
		timelineById.get("facade-treatment"),
	);
	const minVentAreaCm2 = getRuleValue<number>("ventilation.minVentAreaCm2");
	const cableRows = data.cableSpec?.materials?.map((row) => ({
		material: row.material,
		dimension: row.dimension,
		quantity: row.quantity,
	})) ?? [];
	const ventRows = getPhaseRows(data, "systems").filter((row) =>
		/vent|galler|duct|kanal/i.test(`${row.material} ${row.dimension}`),
	);
	// electricalBom validation (Issue 106): the enrichment chain already knows
	// which wiring sits in the hot zone and needs 180°C silicone cable. Surface
	// that count so the systems power substep can call it out instead of the
	// builder discovering a melted PVC run after the fact.
	const hotZoneCableCount =
		data.electricalBom?.bom.filter((row) => row.zone === "hot").length ?? 0;

	const kickoffBlockers = uniqueGuideText([
		...permitMessages,
		...permitChecklist,
		...placementChecklist,
		data.drainage?.requiresFloorDrain ? data.drainage.instructionLabel : "",
		...professionalWarnings,
	]);

	const steps: ProjectBuildGuideStepDraft[] = [
		createGuideStep({
			id: "foundation",
			title: buildGuideText("domain_build_foundationTitle"),
			purpose: buildGuideText("domain_build_guide_foundationPurpose"),
			prerequisites: uniqueGuideText([
				permitData?.permitLabel
					? permitData.status === "ok"
						? buildGuideText("domain_build_guide_foundationPrereqPermitOk", {
								permitLabel: permitData.permitLabel.toLowerCase(),
							})
						: buildGuideText(
								"domain_build_guide_foundationPrereqPermitRequired",
								{
									permitLabel: permitData.permitLabel.toLowerCase(),
								},
							)
					: "",
				data.placement
					? buildGuideText("domain_build_guide_foundationPrereqPlacement")
					: "",
			]),
			substeps: [
				{
					title: buildGuideText("domain_build_foundationTitle"),
					measurements: summarizePlinthWarnings(data),
					mistakeCallout:
						(data.foundation?.warningCount ?? 0) > 0
							? {
									severity: "quality",
									wrong: buildGuideText(
										"domain_build_guide_calloutPlinthWrong",
									),
									right: buildGuideText(
										"domain_build_guide_calloutPlinthRight",
									),
								}
							: undefined,
					checkpoints: uniqueGuideText([
						data.foundation
							? buildGuideText(
									"domain_build_guide_foundationCheckpointMarkPiers",
									{ count: data.foundation.totalPiers },
								)
							: buildGuideText(
									"domain_build_guide_foundationCheckpointMarkPiersFallback",
								),
						data.foundation
							? buildGuideText(
									"domain_build_guide_foundationCheckpointBeams",
									{ count: data.foundation.numBeams },
								)
							: "",
						data.drainage
							? buildGuideText(
									"domain_build_guide_foundationCheckpointDrainLayer",
								)
							: "",
						(data.foundation?.warningCount ?? 0) > 0
							? buildGuideText(
									"domain_build_guide_foundationCheckpointPlinthWarning",
									{ count: data.foundation!.warningCount },
								)
							: "",
					]).map((label, index) => ({
						id:
							["mark-out", "beams", "drain-layer", "plinth-warning"][index] ??
							`foundation-${index}`,
						label,
					})),
				},
			],
			materialsSummary: data.foundation
				? [
						buildGuideText("domain_build_guide_foundationMaterialsPiers", {
							count: data.foundation.totalPiers,
						}),
						buildGuideText("domain_build_guide_foundationMaterialsBeams", {
							count: data.foundation.numBeams,
							length: data.foundation.totalBeamLengthLm.toFixed(1),
						}),
						...summarizePhase(
							data,
							"foundation",
							buildGuideText("domain_build_guide_foundationFallbackMaterials"),
						),
					]
				: summarizePhase(
						data,
						"foundation",
						buildGuideText("domain_build_guide_foundationFallbackMaterials"),
					),
			cutSummary: summarizeCuts(
				data,
				["bärlina", "bjälk"],
				buildGuideText("domain_build_guide_foundationFallbackCuts"),
			),
			risks: uniqueGuideText([
				data.drainage?.instructionLabel ?? "",
				buildGuideText("domain_build_guide_foundationRiskSkew"),
			]),
			controls: uniqueGuideText([
				data.placement
					? buildGuideText("domain_build_guide_foundationControlDiagonal")
					: "",
				data.drainage?.pipeRecommendation ?? "",
			]),
			tools: getTools(
				"foundation",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds: [],
			timeline: timelineById.get("foundation"),
			...getBadge(
				"foundation",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
	];

	const hasDeckMaterials =
		data.materials.some((row) => row.phase === "deck") ||
		(data.materialsByPhase?.some(
			(group) => group.phase === "deck" && group.rows.length > 0,
		) ??
			false);

	if (hasDeckMaterials) {
		steps.push(
			createGuideStep({
				id: "deck",
				title: buildGuideText("domain_build_deckTitle"),
				purpose: buildGuideText("domain_build_guide_deckPurpose"),
				prerequisites: [
					buildGuideText("domain_build_guide_deckPrereq"),
				],
				substeps: [
					{
						title: buildGuideText("domain_build_guide_deckSubstepBearers"),
						crew: {
							size: "duo",
							reason: buildGuideText("domain_build_guide_crewReasonDeckBearers"),
						},
						measurements: data.deck
							? [
									buildGuideText("domain_build_guide_deckMeasurementBearers", {
										count: data.deck.bearers.count,
										dimension: data.deck.bearers.dimensionLabel,
									}),
								]
							: undefined,
						checkpoints: [
							{
								id: "deck-layout",
								label: buildGuideText(
									"domain_build_guide_deckCheckpointLayout",
								),
							},
						],
					},
					{
						title: buildGuideText("domain_build_guide_deckSubstepJoists"),
						measurements: data.deck
							? [
									buildGuideText("domain_build_guide_deckMeasurementJoists", {
										count: data.deck.joists.count,
										dimension: data.deck.joists.dimensionLabel,
										cc: data.deck.joists.ccMm,
									}),
								]
							: undefined,
						checkpoints: [
							{
								id: "deck-cc",
								label: data.deck
									? buildGuideText("domain_build_guide_deckCheckpointCc", {
											cc: data.deck.joists.ccMm,
										})
									: buildGuideText(
											"domain_build_guide_deckCheckpointCcFallback",
										),
							},
						],
					},
					{
						title: buildGuideText("domain_build_guide_deckSubstepBoards"),
						mistakeCallout: {
							severity: "quality",
							wrong: buildGuideText("domain_build_guide_calloutDeckBoardWrong"),
							right: buildGuideText("domain_build_guide_calloutDeckBoardRight"),
						},
						materialsSummary: summarizePhaseMatches(
							data,
							"deck",
							[/trall/i, /board/i],
							buildGuideText("domain_build_guide_deckBoardMaterialFallback"),
						),
						checkpoints: [
							{
								id: "deck-boards",
								label: data.deck
									? buildGuideText("domain_build_guide_deckCheckpointBoards", {
											count: data.deck.boards.count,
										})
									: buildGuideText(
											"domain_build_guide_deckCheckpointBoardsFallback",
										),
							},
						],
					},
				],
				materialsSummary: summarizePhase(
					data,
					"deck",
					buildGuideText("domain_build_guide_deckFallbackMaterials"),
				),
				cutSummary: summarizeCuts(
					data,
					["trall", "regel", "barlina"],
					buildGuideText("domain_build_guide_deckFallbackCuts"),
				),
				risks: [
					buildGuideText("domain_build_guide_deckRiskSupport"),
					buildGuideText("domain_build_guide_deckRiskGap"),
				],
				controls: [
					buildGuideText("domain_build_guide_deckControlDiagonal"),
					buildGuideText("domain_build_guide_deckControlSpacers"),
				],
				tools: getTools(
					"deck",
					thermalInputs.heaterType,
					thermalInputs.includesShower,
					t,
				),
				dependencyIds: ["foundation"],
				timeline: timelineById.get("deck"),
				...getBadge(
					"deck",
					thermalInputs.heaterType,
					thermalInputs.includesShower,
					t,
				),
			}),
		);
	}

	if (data.drainage) {
		steps.push(
			createGuideStep({
				id: "drainage",
				title: buildGuideText("domain_build_drainageTitle"),
				purpose: buildGuideText("domain_build_guide_drainagePurpose"),
				prerequisites: [
					buildGuideText("domain_build_guide_drainagePrereq"),
				],
				substeps: [
					{
						title: buildGuideText("domain_build_drainageTitle"),
						checkpoints: uniqueGuideText([
							thermalInputs.includesShower
								? buildGuideText(
										"domain_build_guide_drainageCheckpointFloorDrain",
									)
								: buildGuideText(
										"domain_build_guide_drainageCheckpointGroundDrain",
									),
							data.drainage.slopeRule,
							data.drainage.pipeRecommendation,
						]).map((label, index) => ({
							id:
								["floor-drain", "slope", "pipe"][index] ??
								`drainage-${index}`,
							label,
						})),
					},
				],
				materialsSummary: [
					data.drainage.instructionLabel,
					...summarizePhase(
						data,
						"foundation",
						buildGuideText("domain_build_guide_drainageFallbackMaterials"),
					),
				],
				cutSummary: [
					buildGuideText("domain_build_guide_drainageCutNoSeparate"),
				],
				risks: uniqueGuideText([
					data.drainage.requiresFloorDrain
						? buildGuideText("domain_build_guide_drainageRiskFloorDrain")
						: "",
					...data.drainage.foundationAdvice,
				]),
				controls: uniqueGuideText([
					thermalInputs.includesShower
						? buildGuideText("domain_build_guide_drainageControlShower")
						: buildGuideText("domain_build_guide_drainageControlGround"),
				]),
				tools: getTools(
					"drainage",
					thermalInputs.heaterType,
					thermalInputs.includesShower,
					t,
				),
				dependencyIds: ["foundation"],
				timeline: timelineById.get("drainage"),
				...getBadge(
					"drainage",
					thermalInputs.heaterType,
					thermalInputs.includesShower,
					t,
				),
			}),
		);
	}

	steps.push(
		createGuideStep({
			id: "framing",
			title: buildGuideText("domain_build_framingTitle"),
			purpose: buildGuideText("domain_build_guide_framingPurpose"),
			prerequisites: [buildGuideText("domain_build_guide_framingPrereq")],
			substeps: [
				{
					title: buildGuideText("domain_build_guide_framingSubstepLayout"),
					crew: {
						size: "duo",
						reason: buildGuideText("domain_build_guide_crewReasonWallFrames"),
					},
					tools: getTools(
						"framing",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "framing--substep-01", roofKind },
					),
					checkpoints: [
						{
							id: "framing-square",
							label: buildGuideText(
								"domain_build_guide_framingCheckpointSquare",
							),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_framingSubstepOpenings"),
					crew: hasDoorOpening
						? {
								size: "duo",
								reason: buildGuideText(
									"domain_build_guide_crewReasonGlassDoor",
								),
							}
						: undefined,
					mistakeCallout: hasDoorOpening
						? {
								severity: "safety",
								wrong: buildGuideText("domain_build_guide_calloutDoorWrong"),
								right: buildGuideText("domain_build_guide_calloutDoorRight"),
							}
						: undefined,
					tools: getTools(
						"framing",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "framing--substep-02", roofKind },
					),
					checkpoints: [
						{
							id: "framing-openings",
							label: buildGuideText(
								"domain_build_guide_framingCheckpointOpenings",
								{ count: openingCount },
							),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_framingSubstepBrace"),
					tools: hasPartitionBlocking
						? getTools(
								"framing",
								thermalInputs.heaterType,
								thermalInputs.includesShower,
								t,
								{ substepId: "framing--substep-01", roofKind },
							)
						: undefined,
					materialsSummary: hasPartitionBlocking
						? [
								buildGuideText(
									"domain_build_guide_framingMaterialPartitionSupport",
									{
										count:
											data.partitionWallFraming?.requiredBlockingCount ?? 0,
									},
								),
							]
						: undefined,
					checkpoints: hasPartitionBlocking
						? [
								{
									id: "framing-partition-support",
									label: buildGuideText(
										"domain_build_guide_framingCheckpointPartitionSupport",
										{
											count:
												data.partitionWallFraming?.requiredBlockingCount ?? 0,
										},
									),
								},
							]
						: [],
				},
			],
			materialsSummary: [
				...(hasPartitionBlocking
					? [
							buildGuideText(
								"domain_build_guide_framingMaterialPartitionSupport",
								{
									count: data.partitionWallFraming?.requiredBlockingCount ?? 0,
								},
							),
						]
					: []),
				...summarizePhase(
					data,
					"framing",
					buildGuideText("domain_build_guide_framingFallbackMaterials"),
				),
			],
			cutSummary: hasPartitionBlocking
				? [
						...data.partitionWallFraming!.cuts.map(
							(row): BuildGuideText => ({
								cutRow: {
									part: row.part,
									count: row.count,
									cutLength: row.cutLength,
								},
							}),
						),
						...summarizeCuts(
							data,
							["regel", "stolpe", "syll"],
							buildGuideText("domain_build_guide_framingFallbackCuts"),
						),
					]
				: summarizeCuts(
						data,
						["regel", "stolpe", "syll"],
						buildGuideText("domain_build_guide_framingFallbackCuts"),
					),
			risks: uniqueGuideText([
				buildGuideText("domain_build_guide_framingRiskOpenings"),
				hasPartitionBlocking
					? buildGuideText("domain_build_guide_framingRiskPartition")
					: "",
			]),
			controls: uniqueGuideText([
				buildGuideText("domain_build_guide_framingControlBrace"),
				hasPartitionBlocking
					? buildGuideText("domain_build_guide_framingControlPartition")
					: "",
			]),
			tools: getTools(
				"framing",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds: data.drainage
				? ["foundation", "drainage"]
				: ["foundation"],
			timeline: timelineById.get("framing"),
			...getBadge(
				"framing",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
		createGuideStep({
			id: "roof",
			title: buildGuideText("domain_build_roofTitle"),
			purpose: buildGuideText("domain_build_guide_roofPurpose"),
			prerequisites: [buildGuideText("domain_build_guide_roofPrereq"), ...(data.constructionBasis ? [buildGuideText("domain_build_constructionRoofPrereq")] : [])],
			substeps: [
				{
					title: buildGuideText("domain_build_guide_roofSubstepJoists"),
					crew: {
						size: "duo",
						reason: buildGuideText("domain_build_guide_crewReasonRoofFrames"),
					},
					tools: getTools(
						"roof",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "roof--substep-01", roofKind },
					),
					checkpoints: [
						{
							id: "roof-joists",
							label: data.roofJoists
								? buildGuideText("domain_build_guide_roofCheckpointJoists", {
										dimension: data.roofJoists.dimension,
										spacing: data.roofJoists.ccSpacingMm,
									})
								: buildGuideText(
										"domain_build_guide_roofCheckpointJoistsFallback",
									),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_roofSubstepGables"),
					checkpoints: [
						{
							id: "roof-gable-infill",
							label: buildGuideText(
								"domain_build_guide_roofCheckpointGableInfill",
							),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_roofSubstepWeather"),
					crew:
						roofKind === "barrel"
							? {
									size: "duo",
									reason: buildGuideText(
										"domain_build_guide_crewReasonBarrelBands",
									),
								}
							: {
									size: "duo",
									reason: buildGuideText("domain_build_guide_crewReasonRoofLift"),
								},
					tools: getTools(
						"roof",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "roof--substep-03", roofKind },
					),
					materialsSummary:
						roofKind === "barrel"
							? [
									buildGuideText(
										"domain_build_guide_roofBarrelMaterialBands",
									),
								]
							: undefined,
					checkpoints: [
						{
							id: "roof-weather",
							label: data.roofAssembly
								? buildGuideText("domain_build_guide_roofCheckpointWeather", {
										layers: data.roofAssembly.layers.join(", "),
									})
								: buildGuideText(
										"domain_build_guide_roofCheckpointWeatherFallback",
									),
						},
					],
				},
			],
			materialsSummary: data.roofAssembly
				? [
						buildGuideText("domain_build_guide_roofMaterialsLayers", {
							layers: data.roofAssembly.layers.join(", "),
						}),
						buildGuideText("domain_build_guide_roofMaterialsAccessories", {
							accessories: data.roofAssembly.requiredAccessories.join(", "),
						}),
						...summarizePhase(
							data,
							"roofExterior",
							buildGuideText("domain_build_guide_roofFallbackMaterials"),
						),
					]
				: summarizePhase(
						data,
						"roofExterior",
						buildGuideText("domain_build_guide_roofFallbackMaterials"),
					),
			cutSummary: summarizeCuts(
				data,
				["tak", "regel"],
				buildGuideText("domain_build_guide_roofFallbackCuts"),
			),
			risks: uniqueGuideText([
				buildGuideText("domain_build_guide_roofRiskMoisture"),
				...(data.roofAssembly?.notes ?? []),
			]),
			controls: uniqueGuideText([
				buildGuideText("domain_build_guide_roofControlFinishWeather"),
				...(data.roofAssembly
					? [
							buildGuideText("domain_build_guide_roofControlAccessories", {
								accessories: data.roofAssembly.requiredAccessories.join(", "),
							}),
						]
					: []),
			]),
			tools: getTools(
				"roof",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds: ["framing"],
			timeline: timelineById.get("roof"),
			...getBadge(
				"roof",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
		createGuideStep({
			id: "facade",
			title: buildGuideText("domain_build_facadeTitle"),
			purpose: buildGuideText("domain_build_guide_facadePurpose"),
			prerequisites: [buildGuideText("domain_build_guide_facadePrereq")],
			substeps: [
				{
					title: buildGuideText("domain_build_guide_facadeSubstepCladding"),
					mistakeCallout: {
						severity: "quality",
						wrong: buildGuideText("domain_build_guide_calloutPanelWrong"),
						right: buildGuideText("domain_build_guide_calloutPanelRight"),
					},
					materialsSummary: summarizePhaseMatches(
						data,
						"roofExterior",
						[/panel/i, /foder/i, /läkt/i],
					),
					checkpoints: [
						{
							id: "facade-air-gap",
							label: buildGuideText(
								"domain_build_guide_facadeCheckpointAirGap",
							),
						},
						{
							id: "facade-front-wall",
							label: buildGuideText(
								"domain_build_guide_facadeCheckpointOpeningsVent",
							),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_facadeSubstepDoor"),
					crew: hasDoorOpening
						? {
								size: "duo",
								reason: buildGuideText(
									"domain_build_guide_crewReasonGlassDoor",
								),
							}
						: undefined,
					mistakeCallout: hasDoorOpening
						? {
								severity: "safety",
								wrong: buildGuideText("domain_build_guide_calloutDoorWrong"),
								right: buildGuideText("domain_build_guide_calloutDoorRight"),
							}
						: undefined,
					tools: hasDoorOpening
						? getTools(
								"framing",
								thermalInputs.heaterType,
								thermalInputs.includesShower,
								t,
								{ substepId: "framing--substep-02", roofKind },
							)
						: undefined,
					checkpoints: hasDoorOpening
						? [
								{
									id: "facade-door-safety",
									label: buildGuideText(
										"domain_build_guide_facadeCheckpointDoorSafety",
									),
								},
							]
						: [],
				},
				// The treatment substep exists exactly when the BOM bought paint.
				// Deriving it from the row rather than from a second copy of the
				// finish config keeps the guide and the purchase list unable to
				// disagree — the BOM is the checklist.
				...(hasExteriorTreatment
					? [
							{
								title: buildGuideText(
									"domain_build_guide_facadeSubstepTreatment",
								),
								mistakeCallout: {
									severity: "quality" as const,
									wrong: buildGuideText(
										"domain_build_guide_calloutTreatmentWrong",
									),
									right: buildGuideText(
										"domain_build_guide_calloutTreatmentRight",
									),
								},
								// By materialKey, not by a Swedish name regex: a sixth
								// treatment would otherwise render a "coat the facade"
								// step with nothing to buy under it.
								materialsSummary: summarizeTreatmentMaterials(data),
								tools: getTools(
									"facadeTreatment",
									thermalInputs.heaterType,
									thermalInputs.includesShower,
									t,
								),
								checkpoints: [
									{
										id: "facade-treatment-coverage",
										label: buildGuideText(
											"domain_build_guide_facadeCheckpointTreatmentCoverage",
										),
									},
									{
										id: "facade-treatment-drying",
										label: buildGuideText(
											"domain_build_guide_facadeCheckpointTreatmentDrying",
										),
									},
								],
							},
						]
					: []),
			],
			materialsSummary: summarizeFacadeMaterials(data),
			cutSummary: summarizeCuts(
				data,
				["panel", "foder"],
				buildGuideText("domain_build_guide_facadeFallbackCuts"),
			),
			risks: [
				buildGuideText("domain_build_guide_facadeRiskMoisture"),
			],
			controls: [
				buildGuideText("domain_build_guide_facadeControlVentilated"),
			],
			tools: getTools(
				"facade",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds: ["roof"],
			timeline: facadeTimeline,
			...getBadge(
				"facade",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
		createGuideStep({
			id: "insulation",
			title: buildGuideText("domain_build_insulationTitle"),
			purpose: buildGuideText("domain_build_guide_insulationPurpose"),
			prerequisites: [buildGuideText("domain_build_guide_insulationPrereq"), ...(data.constructionBasis ? [buildGuideText("domain_build_constructionMoisturePrereq")] : [])],
			substeps: [
				{
					title: buildGuideText(
						"domain_build_guide_insulationSubstepInsulation",
					),
					checkpoints: [
						{
							id: "insulation-fill",
							label: buildGuideText(
								"domain_build_guide_insulationCheckpointFill",
							),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_insulationSubstepFoil"),
					crew: {
						size: "duo",
						reason: buildGuideText("domain_build_guide_crewReasonFoil"),
					},
					mistakeCallout: {
						severity: "quality",
						wrong: buildGuideText("domain_build_guide_calloutFoilWrong"),
						right: buildGuideText("domain_build_guide_calloutFoilRight"),
					},
					tools: getTools(
						"insulation",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "insulation--substep-02", roofKind },
					),
					materialsSummary: summarizePhaseMatches(
						data,
						"interior",
						[/folie/i, /foil/i, /aluminium/i],
						buildGuideText("domain_build_guide_insulationFoilMaterialFallback"),
					),
					checkpoints: [
						{
							id: "insulation-foil",
							label: buildGuideText(
								"domain_build_guide_insulationCheckpointFoil",
							),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_insulationSubstepPanel"),
					mistakeCallout: {
						severity: "quality",
						wrong: buildGuideText("domain_build_guide_calloutPanelWrong"),
						right: buildGuideText("domain_build_guide_calloutPanelRight"),
					},
					tools: getTools(
						"insulation",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "insulation--substep-03", roofKind },
					),
					materialsSummary: summarizePhaseMatches(
						data,
						"interior",
						[/panel/i, /asp/i, /al/i, /läkt/i, /taklist/i],
						buildGuideText("domain_build_guide_insulationPanelMaterialFallback"),
					),
					checkpoints: [
						{
							id: "insulation-ceiling-battens",
							label: buildGuideText(
								"domain_build_guide_insulationCheckpointBattens",
							),
						},
					],
				},
			],
			materialsSummary: summarizePhase(
				data,
				"interior",
				buildGuideText("domain_build_guide_insulationFallbackMaterials"),
			),
			cutSummary: summarizeCuts(
				data,
				["panel", "taklist"],
				buildGuideText("domain_build_guide_insulationFallbackCuts"),
			),
			risks: [
				buildGuideText("domain_build_guide_insulationRiskBarrier"),
				buildGuideText("domain_build_guide_insulationRiskBattens"),
			],
			controls: [
				buildGuideText("domain_build_guide_insulationControlPhoto"),
				buildGuideText("domain_build_guide_insulationControlSequence"),
			],
			tools: getTools(
				"insulation",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds: ["facade"],
			timeline: timelineById.get("insulation"),
			...getBadge(
				"insulation",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
		createGuideStep({
			id: "benches",
			title: buildGuideText("domain_build_benchesTitle"),
			purpose: buildGuideText("domain_build_guide_benchesPurpose"),
			prerequisites: [buildGuideText("domain_build_guide_benchesPrereq")],
			substeps: [
				{
					title: buildGuideText("domain_build_guide_benchesSubstepFrame"),
					crew: {
						size: "duo",
						reason: buildGuideText("domain_build_guide_crewReasonBenchFrame"),
					},
					tools: getTools(
						"benches",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "benches--substep-01", roofKind },
					),
					measurements: data.ergonomics
						? [
								buildGuideText(
									"domain_build_guide_benchesMeasurementHeights",
									{
										upper: data.ergonomics.upperBenchYMm,
										lower: data.ergonomics.lowerBenchYMm,
									},
								),
							]
						: undefined,
					checkpoints: [
						{
							id: "bench-heights",
							label: data.ergonomics
								? buildGuideText("domain_build_guide_benchesCheckpointHeights", {
										upper: data.ergonomics.upperBenchYMm,
										lower: data.ergonomics.lowerBenchYMm,
									})
								: buildGuideText(
										"domain_build_guide_benchesCheckpointHeightsFallback",
									),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_benchesSubstepSeat"),
					mistakeCallout: {
						severity: "quality",
						wrong: buildGuideText("domain_build_guide_calloutBenchesWrong"),
						right: buildGuideText("domain_build_guide_calloutBenchesRight"),
					},
					materialsSummary: summarizePhaseMatches(
						data,
						"interior",
						[/asp/i, /\bal\b/i, /abachi/i, /lav/i, /bänk/i, /panel/i],
						buildGuideText("domain_build_guide_benchesMaterialFallback"),
					),
					checkpoints: [
						{
							id: "bench-finish",
							label: buildGuideText(
								"domain_build_guide_benchesCheckpointFinish",
							),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_benchesSubstepHatch"),
					checkpoints: [
						{
							id: "bench-hatch",
							label: buildGuideText(
								"domain_build_guide_benchesCheckpointHatch",
							),
						},
					],
				},
			],
			materialsSummary: summarizePhase(
				data,
				"interior",
				buildGuideText("domain_build_guide_benchesFallbackMaterials"),
			),
			cutSummary: summarizeCuts(
				data,
				["lav", "bänk"],
				buildGuideText("domain_build_guide_benchesFallbackCuts"),
			),
			risks: [buildGuideText("domain_build_guide_benchesRiskComfort")],
			controls: [buildGuideText("domain_build_guide_benchesControlTrialFit")],
			tools: getTools(
				"benches",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds: ["insulation"],
			timeline: timelineById.get("benches"),
			...getBadge(
				"benches",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
		createGuideStep({
			id: "systems",
			title: buildGuideText("domain_build_systemsTitle"),
			purpose:
				thermalInputs.heaterType === "wood"
					? buildGuideText("domain_build_guide_systemsPurposeWood")
					: buildGuideText("domain_build_guide_systemsPurposeElectric"),
			prerequisites: [buildGuideText("domain_build_guide_systemsPrereq")],
			substeps: [
				{
					title: buildGuideText("domain_build_guide_systemsSubstepPower"),
					crew: {
						size: "duo",
						reason: buildGuideText("domain_build_guide_crewReasonHeaterLift"),
					},
					mistakeCallout: data.heaterClearance
						? {
								severity: "safety",
								wrong: buildGuideText("domain_build_guide_calloutHeaterWrong"),
								right: buildGuideText("domain_build_guide_calloutHeaterRight", {
									side: data.heaterClearance.clearances.sideMm,
									front: data.heaterClearance.clearances.frontMm,
									ceiling: data.heaterClearance.clearances.ceilingMm,
								}),
							}
						: thermalInputs.heaterType === "wood" && data.chimney
							? {
									severity: "safety",
									wrong: buildGuideText(
										"domain_build_guide_calloutHeaterWrong",
									),
									right: buildGuideText(
										"domain_build_guide_calloutHeaterRightWood",
										{ rule: data.chimney.clearanceRule },
									),
								}
							: {
									// Legacy barrel (#071) carries no heaterClearance/chimney slice.
									// Rather than silently drop the safety callout, degrade
									// explicitly so the builder knows the clearance figures are
									// unavailable for this sauna type (Issue 106).
									severity: "safety",
									wrong: buildGuideText("domain_build_guide_calloutHeaterWrong"),
									right: buildGuideText(
										"domain_build_guide_calloutHeaterRightUnavailable",
									),
								},
					tools: getTools(
						"systems",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "systems--substep-01", roofKind },
					),
					materialsSummary: summarizeRowsWithFallback(
						cableRows,
						buildGuideText("domain_build_guide_systemsPowerMaterialFallback"),
					),
					measurements: uniqueGuideText([
						data.cableSpec
							? buildGuideText("domain_build_guide_systemsMeasurementCable", {
									cableType: data.cableSpec.cableType,
									fuseLabel: data.cableSpec.fuseLabel,
									totalCableLength: data.cableSpec.totalCableLengthM,
								})
							: "",
						hotZoneCableCount > 0
							? buildGuideText(
									"domain_build_guide_systemsMeasurementHotZone",
									{ count: hotZoneCableCount },
								)
							: "",
					]),
					checkpoints: uniqueGuideText([
						thermalInputs.heaterType === "electric"
							? buildGuideText("domain_build_guide_systemsCheckpointElectric")
							: buildGuideText("domain_build_guide_systemsCheckpointWood"),
						hotZoneCableCount > 0
							? buildGuideText(
									"domain_build_guide_systemsCheckpointHotZone",
									{ count: hotZoneCableCount },
								)
							: "",
					]).map((label, index) => ({
						id:
							["systems-power", "systems-hot-zone"][index] ??
							`systems-power-${index}`,
						label,
					})),
				},
				{
					title: buildGuideText("domain_build_guide_systemsSubstepVent"),
					mistakeCallout: data.ventilation
						? {
								severity: "safety",
								wrong: buildGuideText("domain_build_guide_calloutVentWrong"),
								right: buildGuideText("domain_build_guide_calloutVentRight", {
									minArea: minVentAreaCm2,
								}),
							}
						: undefined,
					tools: getTools(
						"systems",
						thermalInputs.heaterType,
						thermalInputs.includesShower,
						t,
						{ substepId: "systems--substep-02", roofKind },
					),
					materialsSummary: summarizeRowsWithFallback(
						ventRows,
						buildGuideText("domain_build_guide_systemsVentMaterialFallback", {
							minArea: minVentAreaCm2,
						}),
					),
					measurements: data.ventilation
						? [
								buildGuideText(
									"domain_build_guide_systemsMeasurementVent",
									{
										intake: data.ventilation.intakeYMm,
										exhaust: data.ventilation.exhaustYMm,
										minArea: minVentAreaCm2,
									},
								),
							]
						: undefined,
					checkpoints: uniqueGuideText([
						data.ventilation
							? buildGuideText("domain_build_guide_systemsCheckpointVent", {
									intake: data.ventilation.intakeYMm,
									exhaust: data.ventilation.exhaustYMm,
								})
							: buildGuideText(
									"domain_build_guide_systemsCheckpointVentFallback",
								),
					]).map((label, index) => ({
						id: ["systems-vent"][index] ?? `systems-vent-${index}`,
						label,
					})),
				},
			],
			materialsSummary: summarizePhase(
				data,
				"systems",
				buildGuideText("domain_build_guide_systemsFallbackMaterials"),
			),
			cutSummary: [
				thermalInputs.heaterType === "electric"
					? buildGuideText("domain_build_guide_systemsCutElectric")
					: buildGuideText("domain_build_guide_systemsCutWood"),
			],
			risks: uniqueGuideText([
				thermalInputs.heaterType === "electric"
					? buildGuideText("domain_build_guide_systemsRiskElectric")
					: (data.chimney?.clearanceRule ?? ""),
				data.ventilation?.requiresFan
					? buildGuideText("domain_build_guide_systemsRiskFan")
					: "",
			]),
			controls: uniqueGuideText([
				data.cableSpec
					? buildGuideText("domain_build_guide_systemsControlCableSpec", {
							cableType: data.cableSpec.cableType,
							fuseLabel: data.cableSpec.fuseLabel,
							rcdLabel: data.cableSpec.rcdLabel,
						})
					: "",
				thermalInputs.heaterType === "electric"
					? buildGuideText("domain_build_guide_systemsControlElectric")
					: buildGuideText("domain_build_guide_systemsControlWood"),
			]),
			tools: getTools(
				"systems",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds: ["insulation", "benches"],
			timeline: timelineById.get("systems"),
			...getBadge(
				"systems",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
	);

	if (thermalInputs.heaterType === "wood" && data.chimney) {
		steps.push(
			createGuideStep({
				id: "chimney",
				title: buildGuideText("domain_build_chimneyTitle"),
				purpose: buildGuideText("domain_build_guide_chimneyPurpose"),
				prerequisites: [buildGuideText("domain_build_guide_chimneyPrereq")],
				substeps: [
					{
						title: buildGuideText("domain_build_chimneyTitle"),
						mistakeCallout: {
							severity: "safety",
							wrong: buildGuideText("domain_build_guide_calloutChimneyWrong"),
							right: buildGuideText("domain_build_guide_calloutChimneyRight", {
								rule: data.chimney.clearanceRule,
							}),
						},
						tools: getTools(
							"chimney",
							thermalInputs.heaterType,
							thermalInputs.includesShower,
							t,
							{ substepId: "chimney--substep-01", roofKind },
						),
						materialsSummary: summarizeRowsWithFallback(
							data.chimney.materials.map((row) => ({
								material: row.material,
								dimension: row.dimension,
								quantity: row.quantity,
							})),
						),
						measurements: [
							buildGuideText("domain_build_guide_chimneyMeasurementClearance", {
								rule: data.chimney.clearanceRule,
							}),
						],
						checkpoints: [
							{
								id: "chimney-clearance",
								label: buildGuideText(
									"domain_build_guide_chimneyCheckpointClearance",
									{ rule: data.chimney.clearanceRule },
								),
							},
							{
								id: "chimney-bay-selection",
								label: buildGuideText(
									"domain_build_guide_chimneyCheckpointBay",
								),
							},
							{
								id: "chimney-ceiling-flange",
								label: buildGuideText(
									"domain_build_guide_chimneyCheckpointCeilingFlange",
								),
							},
							{
								id: "chimney-roof-penetration",
								label: buildGuideText(
									"domain_build_guide_chimneyCheckpointRoofPenetration",
								),
							},
							{
								id: "chimney-storm-collar",
								label: buildGuideText(
									"domain_build_guide_chimneyCheckpointStormCollar",
								),
							},
							{
								id: "chimney-cap",
								label: buildGuideText(
									"domain_build_guide_chimneyCheckpointCap",
								),
							},
							{
								id: "chimney-sweeper",
								label: buildGuideText(
									"domain_build_guide_chimneyCheckpointSweeper",
								),
							},
						],
					},
				],
				materialsSummary: [
					buildGuideText("domain_build_guide_chimneyMaterialsSystem", {
						system: data.chimney.recommendedSystem,
						diameter: data.chimney.diameterMm,
						height: (data.chimney.heightMm / 1000).toFixed(1),
					}),
					...data.chimney.materials.map(
						(row): BuildGuideText => ({
							materialRow: {
								material: row.material,
								dimension: row.dimension,
								quantity: row.quantity,
							},
						}),
					),
				],
				cutSummary: [
					buildGuideText("domain_build_guide_chimneyCutCenter"),
					buildGuideText("domain_build_guide_chimneyCutMargin"),
				],
				risks: [
					data.chimney.clearanceRule,
					buildGuideText("domain_build_guide_chimneyRiskBeam"),
					buildGuideText("domain_build_guide_chimneyRiskFireSeal"),
					buildGuideText("domain_build_guide_chimneyRiskStormCollar"),
					data.chimney.maintenanceNote,
				],
				controls: [
					buildGuideText("domain_build_guide_chimneyControlClearance"),
					buildGuideText("domain_build_guide_chimneyControlAnchors"),
					buildGuideText("domain_build_guide_chimneyControlStormCollar"),
					buildGuideText("domain_build_guide_chimneyControlCap"),
					buildGuideText("domain_build_guide_chimneyControlDocuments"),
				],
				tools: getTools(
					"chimney",
					thermalInputs.heaterType,
					thermalInputs.includesShower,
					t,
				),
				dependencyIds: ["systems", "roof"],
				timeline: timelineById.get("chimney"),
				...getBadge(
					"chimney",
					thermalInputs.heaterType,
					thermalInputs.includesShower,
					t,
				),
			}),
		);
	}

	steps.push(
		createGuideStep({
			id: "handover",
			title: buildGuideText("domain_build_handoverTitle"),
			purpose: buildGuideText("domain_build_guide_handoverPurpose"),
			prerequisites: [
				thermalInputs.heaterType === "wood"
					? buildGuideText("domain_build_guide_handoverPrereqWood")
					: buildGuideText("domain_build_guide_handoverPrereqElectric"),
			],
			substeps: [
				{
					title: buildGuideText("domain_build_guide_handoverSubstepSelfCheck"),
					checkpoints: uniqueGuideText([
						...(permitData?.checklist ?? []),
						thermalInputs.heaterType === "wood"
							? buildGuideText("domain_build_guide_handoverCheckpointWood")
							: buildGuideText(
									"domain_build_guide_handoverCheckpointElectric",
								),
					]).map((label, index) => ({
						id: `handover-selfcheck-${index}`,
						label,
					})),
				},
				{
					title: buildGuideText(
						"domain_build_guide_handoverSubstepDocumentation",
					),
					checkpoints: [
						{
							id: "handover-doc-photos",
							label: buildGuideText(
								"domain_build_guide_handoverCheckpointDocPhotos",
							),
						},
						{
							id: "handover-doc-certificates",
							label:
								thermalInputs.heaterType === "electric"
									? buildGuideText(
											"domain_build_guide_handoverCheckpointDocElectric",
										)
									: buildGuideText(
											"domain_build_guide_handoverCheckpointDocWood",
										),
						},
					],
				},
				{
					title: buildGuideText("domain_build_guide_handoverSubstepFirstBath"),
					mistakeCallout: {
						severity: "quality",
						wrong: buildGuideText(
							"domain_build_guide_calloutFirstBathWrong",
						),
						right: buildGuideText(
							"domain_build_guide_calloutFirstBathRight",
						),
					},
					checkpoints: [
						{
							id: "handover-first-cure",
							label: buildGuideText(
								"domain_build_guide_handoverCheckpointFirstCure",
							),
						},
						{
							id: "handover-first-bath",
							label: buildGuideText(
								"domain_build_guide_handoverCheckpointFirstBath",
							),
						},
					],
				},
			],
			materialsSummary: [
				buildGuideText("domain_build_guide_handoverMaterialNone"),
			],
			cutSummary: [buildGuideText("domain_build_guide_handoverCutNone")],
			risks: [buildGuideText("domain_build_guide_handoverRiskLate")],
			controls: uniqueGuideText([...placementChecklist, ...permitMessages]),
			tools: getTools(
				"handover",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
			dependencyIds:
				thermalInputs.heaterType === "wood" && data.chimney
					? ["chimney"]
					: ["systems"],
			timeline: timelineById.get("handover"),
			...getBadge(
				"handover",
				thermalInputs.heaterType,
				thermalInputs.includesShower,
				t,
			),
		}),
	);

	const electricianLeadTime = timelineById.get("systems")?.leadTimeWeeks;
	const plumbingLeadTime = timelineById.get("drainage")?.leadTimeWeeks;
	const fireplaceLeadTime = timelineById.get("chimney")?.leadTimeWeeks;
	const plumbingProfessionalStep = data.professionalGuidance?.steps.find(
		(step) => step.stepId === "drainage" && step.requiresProfessional,
	);
	const needsFireplacePermitGate =
		thermalInputs.heaterType === "wood" &&
		(permitData?.requiresFireplaceNotification ?? false);
	const gates: BuildGuideGate[] = [];

	if (thermalInputs.heaterType === "electric") {
		gates.push(
			createGuideGate(
				{
					id: "gate-electrician-roughin",
					kind: "booking",
					blocksStepId: "insulation",
					title: buildGuideText("domain_build_gate_electricianTitle"),
					purpose: buildGuideText("domain_build_gate_electricianPurpose"),
					checklist: createChecklistDrafts("gate-electrician", [
						buildGuideText("domain_build_gate_electricianChecklistFeed"),
						buildGuideText("domain_build_gate_electricianChecklistHeaterStub"),
						buildGuideText("domain_build_gate_electricianChecklistConduit"),
					]),
					leadTimeLabel: buildGuideText(
						"domain_build_gate_electricianLeadTime",
						{
							min: electricianLeadTime?.min ?? 2,
							max: electricianLeadTime?.max ?? 4,
						},
					),
					leadTimeWeeks: electricianLeadTime,
					dependencyIds: ["facade"],
					tools: [],
					professionalBadge: "",
					professionalReason: "",
				},
				t,
			),
		);
	}

	if (thermalInputs.includesShower && data.drainage && plumbingProfessionalStep) {
		gates.push(
			createGuideGate(
				{
					id: "gate-plumbing-drainage",
					kind: "booking",
					blocksStepId: "drainage",
					title: buildGuideText("domain_build_gate_plumbingTitle"),
					purpose: buildGuideText("domain_build_gate_plumbingPurpose"),
					checklist: createChecklistDrafts("gate-plumbing", [
						data.drainage.instructionLabel,
						data.drainage.slopeRule,
						data.drainage.pipeRecommendation,
					]),
					leadTimeLabel: buildGuideText("domain_build_gate_plumbingLeadTime", {
						min: plumbingLeadTime?.min ?? 1,
						max: plumbingLeadTime?.max ?? 2,
					}),
					leadTimeWeeks: plumbingLeadTime,
					dependencyIds: ["foundation"],
					tools: [],
					professionalBadge: "",
					professionalReason: "",
					controls: [plumbingProfessionalStep.reason],
				},
				t,
			),
		);
	}

	if (needsFireplacePermitGate) {
		gates.push(
			createGuideGate(
				{
					id: "gate-fireplace-notification",
					kind: "permit",
					blocksStepId: "foundation",
					title: buildGuideText("domain_build_gate_fireplaceNotificationTitle"),
					purpose: buildGuideText("domain_build_gate_fireplaceNotificationPurpose"),
					checklist: createChecklistDrafts("gate-fireplace-notification", [
						...permitMessages,
						...permitChecklist,
						buildGuideText(
							"domain_build_gate_fireplaceNotificationChecklistSubmit",
						),
					]),
					leadTimeLabel: buildGuideText(
						"domain_build_gate_fireplaceNotificationLeadTime",
						{
							min: fireplaceLeadTime?.min ?? 2,
							max: fireplaceLeadTime?.max ?? 6,
						},
					),
					leadTimeWeeks: fireplaceLeadTime,
					dependencyIds: [],
					tools: [],
					professionalBadge: "",
					professionalReason: "",
				},
				t,
			),
			createGuideGate(
				{
					id: "gate-fireplace-start-clearance",
					kind: "permit",
					blocksStepId: "systems",
					title: buildGuideText("domain_build_gate_fireplaceStartTitle"),
					purpose: buildGuideText("domain_build_gate_fireplaceStartPurpose"),
					checklist: createChecklistDrafts("gate-fireplace-start", [
						buildGuideText("domain_build_gate_fireplaceStartChecklistWait"),
						data.chimney?.clearanceRule ??
							buildGuideText(
								"domain_build_gate_fireplaceStartChecklistClearanceFallback",
							),
						buildGuideText(
							"domain_build_gate_fireplaceStartChecklistDocuments",
						),
					]),
					leadTimeLabel: buildGuideText(
						"domain_build_gate_fireplaceStartLeadTime",
					),
					dependencyIds: ["benches", "insulation"],
					tools: [],
					professionalBadge: "",
					professionalReason: "",
				},
				t,
			),
			createGuideGate(
				{
					id: "gate-fireplace-inspection",
					kind: "inspection",
					blocksStepId: "handover",
					title: buildGuideText("domain_build_gate_fireplaceInspectionTitle"),
					purpose: buildGuideText("domain_build_gate_fireplaceInspectionPurpose"),
					checklist: createChecklistDrafts("gate-fireplace-inspection", [
						buildGuideText(
							"domain_build_gate_fireplaceInspectionChecklistBooking",
						),
						buildGuideText(
							"domain_build_gate_fireplaceInspectionChecklistProtocol",
						),
						buildGuideText(
							"domain_build_gate_fireplaceInspectionChecklistNoFire",
						),
					]),
					leadTimeLabel: buildGuideText(
						"domain_build_gate_fireplaceInspectionLeadTime",
					),
					dependencyIds:
						thermalInputs.heaterType === "wood" && data.chimney
							? ["chimney"]
							: ["systems"],
					tools: [],
					professionalBadge: "",
					professionalReason: "",
				},
				t,
			),
		);
	}

	const modifiedSteps: ProjectBuildGuideStepDraft[] = engineType
		? (getEngine(engineType).modifyBuildGuideSteps?.(steps, {
					data,
					width,
					length,
					openings,
					thermalInputs,
					market,
				}) ?? steps)
		: steps;
	const sequence = insertGatesBeforeSteps(
		modifiedSteps.map((step) => serializeGuideStep(step, t)),
		gates,
	);
	const highlightDrafts: Array<{ label: BuildGuideText; value: BuildGuideText }> =
		[];
	// A build with no foundation (the indoor sauna, issue 175) has none to lock.
	const kickoffSummary = buildGuideText(
		data.foundation
			? "domain_build_guide_kickoffSummary"
			: "domain_build_guide_kickoffSummaryNoFoundation",
	);

	if (data.foundation) {
		highlightDrafts.push({
			label: buildGuideText("domain_build_guide_kickoffHighlightFoundation"),
			value: buildGuideText(
				"domain_build_guide_kickoffHighlightFoundationValue",
				{ count: data.foundation.totalPiers },
			),
		});
	}

	highlightDrafts.push({
		label: buildGuideText("domain_build_guide_kickoffHighlightOpenings"),
		value: buildGuideText(
			"domain_build_guide_kickoffHighlightOpeningsValue",
			{ count: openingCount },
		),
	});

	if (data.timeline) {
		highlightDrafts.push({
			label: buildGuideText("domain_build_guide_kickoffHighlightTime"),
			value: buildGuideText("domain_build_guide_kickoffHighlightTimeValue", {
				hours: data.timeline.totalHours,
			}),
		});
	}

	if (data.professionalGuidance?.professionalStepCount) {
		highlightDrafts.push({
			label: buildGuideText("domain_build_guide_kickoffHighlightProfessional"),
			value: buildGuideText(
				"domain_build_guide_kickoffHighlightProfessionalValue",
				{ count: data.professionalGuidance.professionalStepCount },
			),
		});
	}

	return {
		kickoff: {
			title: toLegacyGuideText(
				buildGuideText("domain_build_guide_kickoffTitle"),
				t,
			),
			titleText: buildGuideText("domain_build_guide_kickoffTitle"),
			summary: toLegacyGuideText(kickoffSummary, t),
			summaryText: kickoffSummary,
			blockers: kickoffBlockers.map((value) => toLegacyGuideText(value, t)),
			blockerTexts: kickoffBlockers,
			highlights: highlightDrafts.map((item) => ({
				label: toLegacyGuideText(item.label, t),
				labelText: maybeGuideText(item.label),
				value: toLegacyGuideText(item.value, t),
				valueText: maybeGuideText(item.value),
			})),
		},
		totals: {
			areaSqm,
			totalHours: data.timeline?.totalHours ?? null,
			weekendEstimate: data.timeline?.weekendEstimate ?? null,
			calendarEstimateLabel: data.timeline?.calendarEstimateLabel,
			leadTimeWeeks: data.timeline?.totalLeadTimeWeeks ?? null,
			professionalStepCount:
				data.professionalGuidance?.professionalStepCount ?? 0,
			estimatedProfessionalCostSek:
				data.professionalGuidance?.estimatedTotalCostSek ?? null,
			openingCount,
		},
		steps: sequence,
	};
}
